import { CommonModule } from "@angular/common";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, OnDestroy } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TableModule } from "primeng/table";
import { Observable, Subject, mergeMap, takeUntil, tap } from "rxjs";
import { AppService } from "src/app/services/app.service";
import { CustomersService } from "src/app/services/customers.service";
import { LocalizationService, LocalizedNumberFormat } from "src/app/shared/localization/localization.service";
import { ILastAccessedCaseRow } from "src/app/shared/models";
import { UtilsService } from "src/app/shared/utils.service";

@Component({
    selector: "app-last-accessed-cases",
    templateUrl: "last-accessed-cases.component.html",
    styleUrls: ["last-accessed-cases.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TranslateModule, TableModule]
})
export class LastAccessedCasesComponent implements OnInit, OnDestroy {
    constructor(private customersService: CustomersService
        , private localizationService: LocalizationService
        , public translateService: TranslateService
        , public utils: UtilsService
        , private changeDetectorRef: ChangeDetectorRef
        , private appService: AppService) {
    }

    caseList$: Observable<ILastAccessedCaseRow[]>
    numberFormat: LocalizedNumberFormat;
    dateTimeFormat: string;
    isLoading = true;
    totalNumberOfItems = 0;
    first = 0;
    numberOfItemsPerPage = 10;

    private readonly unsubscribe$: Subject<any> = new Subject();

    get isEnglish(): boolean {
        const lang = (this.translateService.currentLang
            || this.translateService.defaultLang || '').toLowerCase();
        return lang === 'en';
    }

    get TABLE_COLUMNS(): TableColumn[] {
        return [
            { name: 'debtorReference1', type: 'any', translationTag: 'CaseRow.CustomerReference' }
            , { name: 'caseNumber', type: 'any', translationTag: 'General.CaseNumber' }
            , { name: 'debtorName', type: 'any', translationTag: 'General.DebtorName' }
            , { name: 'customerNumber', type: 'any', translationTag: 'General.CustomerNumber' }
            , { name: 'customerName', type: 'any', translationTag: 'General.CustomerName' }
            , { name: this.isEnglish ? 'statusEn' : 'status', type: 'any', translationTag: 'CaseRow.Status' }
            , { name: 'lastAccessedDate', type: 'datetime', translationTag: 'LastAccessedCases.LastAccessedDate' }
        ]
    }

    ngOnInit(): void {
        this.isLoading = true;

        this.localizationService.settings$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(_ => {
                this.numberFormat = _.numberPipeFormat;
                this.dateTimeFormat = _.dateTimePipeFormat;
                this.changeDetectorRef.markForCheck();
            });

        this.caseList$ = this.appService.customerNumber$.pipe(
            takeUntil(this.unsubscribe$)
            , tap(() => {
                this.isLoading = true;
                this.changeDetectorRef.markForCheck();
            })
            , mergeMap((customerNumber) => this.customersService.getLastAccessedCases(customerNumber, 40))
            , tap((cases) => {
                this.totalNumberOfItems = cases.length;
                this.first = 0;
                this.isLoading = false;
                this.changeDetectorRef.markForCheck();
            })
        )
    }

    fillToPageSize(rows: ILastAccessedCaseRow[] = []) {
        const arrayCopy: ILastAccessedCaseRow[] = [...rows];
        for (let i = arrayCopy.length; i < this.numberOfItemsPerPage; i++)
            {arrayCopy.push({} as ILastAccessedCaseRow); }
        return arrayCopy;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(null);
        this.unsubscribe$.complete();
    }
}

interface TableColumn {
  name: string;
  type: string;
  translationTag: string;
}